/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import Icon from "uis/Icon";
import GoogleLogo from "../../../assets/imgs/google-logo.png";
import { TCalendarVisit, TVisit, TVisitor } from "apis/visitors/type";
import Header from "pages/Header";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { theme } from "styles/theme";
import Button from "uis/Button";
import InputField from "uis/InputField";
import { getEngTime_v2, getKorDate, getKorTime_v2 } from "utils/date";
import { getVisitRecord } from "apis/visitors";
import DatePicker from "react-datepicker";
import SearchManagerItem from "uis/layout/search-manager-item";
import { addPersonalCalendar, updatePersonalCalendar } from "apis/visitors/personal-calendar";
import { decrypt } from "utils/crypto";
import { LoadingModal } from "uis/modal";
import { useRecoilValue } from "recoil";
import { member, userInfo, util } from "recoil/recoilState";
import { translate } from "./translate";
import TranslationBtn from "uis/translation-btn";
import { TMember } from "types/user.types";
import { getPersonalCalendarConnection } from "apis/personal-calendar";
import { searchMembers } from "apis/members";

const Root = styled.div`
  background-color: #f9fbff;
  flex: 1;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const ModalContainer = styled.div`
  filter: drop-shadow(0px 4px 20px rgba(121, 145, 169, 0.15));
  width: 420px;
  min-height: 100px;
  background-color: white;
  border-radius: 20px;
  margin: 100px auto 0 auto;
  display: flex;
  flex-direction: column;
  padding: 40px;
  gap: 40px;
  margin-bottom: 150px;
`;

const Title = styled.p`
  font-size: 20px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Label = styled.p`
  font-size: 16px;
  font-weight: 700;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PickerBtn = styled.div`
  display: flex;
  width: 160px;
  justify-content: space-between;
  padding: 9px 18px;
  align-items: center;
  background-color: ${theme.neutral50};
  border-radius: 10px;

  &:hover {
    background-color: ${theme.neutral100};
  }
`;

const isValidEmail = (email: string) => {
  const regex = new RegExp("[a-z0-9]+@[a-z]+.[a-z]{2,3}");
  return regex.test(email);
};

const AddPersonalCalendar = () => {
  const id = decrypt(decodeURIComponent(`${useParams().id}`));
  const status = decodeURIComponent(`${useParams().status}`);
  const [visitRecord, setVisitRecord] = useState<TVisit>();
  const [calendar, setCalendar] = useState<TCalendarVisit>();
  const [loading, setLoading] = useState<boolean>(false);
  const [isCanAccess, setIsCanAccess] = useState<boolean>(false);
  const memberInfo = useRecoilValue(member);
  const { language } = useRecoilValue(util);

  useEffect(() => {
    if (memberInfo) {
      getPersonalCalendarConnection().then((res) => {
        setIsCanAccess(res.success);
        if (!res.success) {
          alert("개인 캘린더 연동이 필요한 기능입니다.");
          return;
        }

        if (id) {
          getVisitRecord(id).then((res) => {
            if (res) {
              const tempTime = new Date(res.visitDatetime);
              setVisitRecord(res);
              setCalendar({
                visitRecordId: id,
                endTime: new Date(tempTime.getTime() + 60 * 60 * 1000).toISOString(),
                startTime: res.visitDatetime,
                title: `${memberInfo.nickname} ↔ ${res.visitors[0].name}${
                  res.visitors.length > 1 ? ` 외 ${res.visitors.length - 1}명` : ""
                } 방문 일정`,
                description: "",
                attendees: [],
              });
            } else {
              alert("권한이 없는 접근입니다.");
              window.location.href = "/";
            }
          });
        }
      });
    }
  }, [memberInfo]);

  return (
    <Root>
      <Header />

      {visitRecord && isCanAccess && (
        <ModalContainer>
          <div>
            <TranslationBtn
              allowClick
              styles={{
                container: css`
                  border-radius: 100px;
                  width: max-content;
                  margin-left: auto;
                  margin-bottom: 24px;
                  cursor: pointer;

                  &:hover {
                    background-color: ${theme.neutral100};
                  }
                `,
                text: css`
                  font-size: 16px;
                `,
              }}
              iconSize={20}
            />
            <Title>
              <img src={GoogleLogo} width={24} />
              {translate["캘린더 일정 추가"][language]}
            </Title>
          </div>
          <TitleSection
            title={calendar?.title}
            onChange={(value: string) => {
              setCalendar((prev) => {
                return { ...prev, title: value };
              });
            }}
          />
          <DateSection dateTime={visitRecord.visitDatetime} />
          <TimeSection
            dateTime={visitRecord.visitDatetime}
            endTime={new Date(`${calendar?.endTime}`)}
            onChange={(end) => {
              setCalendar((prev) => {
                return { ...prev, endTime: end.toISOString() };
              });
            }}
          />
          <AttendeeSection
            visitors={visitRecord.visitors}
            managers={visitRecord.visitRecordManagers}
            onChange={(users) => {
              setCalendar((prev) => {
                return { ...prev, attendees: users };
              });
            }}
          />
          <MemoSection
            onChange={(value) => {
              setCalendar((prev) => {
                return { ...prev, description: value };
              });
            }}
          />
          <ButtonGroup
            onHandleSkip={() => (window.location.href = `/visitors/${encodeURIComponent("나의 방문 관리")}`)}
            onHandleRegister={async () => {
              setLoading(true);
              if (calendar) {
                if (status === "create") {
                  const res = await addPersonalCalendar(calendar);
                  if (res.success) {
                    alert("등록이 완료되었습니다.");
                    window.location.href = `/visitors/${encodeURIComponent("나의 방문 관리")}`;
                  }
                }
                if (status === "edit") {
                  const res = await updatePersonalCalendar(calendar);

                  if (res.success) {
                    alert("일정 수정이 완료되었습니다.");
                    window.location.href = `/visitors/${encodeURIComponent("나의 방문 관리")}`;
                  }
                }
              }
            }}
          />
        </ModalContainer>
      )}
      <LoadingModal modalIsOpen={loading} />
    </Root>
  );
};

export const TitleSection = ({ title, onChange }: { title?: string; onChange?: (title: string) => void }) => {
  const { language } = useRecoilValue(util);
  return (
    <SectionContainer>
      <Label>{translate["제목"][language]}</Label>
      <InputField defaultValue={title} onChange={onChange} />
    </SectionContainer>
  );
};

export const DateSection = ({ dateTime }: { dateTime: string }) => {
  const { language } = useRecoilValue(util);
  return (
    <SectionContainer>
      <Label>{translate["방문 일자"][language]}</Label>
      <InputField type="disabled" value={getKorDate(dateTime)} />
    </SectionContainer>
  );
};

export const TimeSection = ({
  dateTime,
  onChange,
  endTime,
}: {
  dateTime: string;
  onChange?: (end: Date) => void;
  endTime?: Date;
}) => {
  const [time, setTime] = useState<Date>(endTime ? endTime : new Date(dateTime));
  const { language } = useRecoilValue(util);

  const tomorrow = new Date(dateTime);
  tomorrow.setHours(23, 45, 0, 0);

  return (
    <SectionContainer>
      <Label>{translate["방문 시간"][language]}</Label>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
        `}
      >
        <InputField
          type="disabled"
          value={language === "ko" ? getKorTime_v2(dateTime) : getEngTime_v2(dateTime)}
          styles={{
            container: css`
              width: 200px;
              flex: 1;
            `,
          }}
        />
        <p>~</p>
        <DatePicker
          selected={time}
          onChange={(date) => {
            if (date) {
              setTime(date);
              onChange?.(date);
            }
          }}
          locale={"ko"}
          showTimeSelect
          showTimeSelectOnly
          timeIntervals={15}
          timeCaption="시간"
          minTime={new Date(dateTime)}
          maxTime={tomorrow}
          customInput={
            <PickerBtn className="example-custom-input">
              <p
                css={css`
                  font-size: 15px;
                `}
              >
                {language === "ko" ? getKorTime_v2(time.toISOString()) : getEngTime_v2(time.toISOString())}
              </p>
              <Icon name="watch" color={theme.neutral400} width={24} height={24} />
            </PickerBtn>
          }
        />
      </div>
    </SectionContainer>
  );
};

export const AttendeeSection = ({
  visitors,
  managers,
  onChange,
}: {
  visitors: TVisitor[];
  managers?: { id: string; isOrganizer: boolean; manager: TMember }[];
  onChange?: (value: string[]) => void;
}) => {
  const [searchMember, setSearchMember] = useState<TMember[]>([]);
  const { language } = useRecoilValue(util);
  const [keyword, setKeyword] = useState<string>();
  const filtered_visitors = visitors.filter((item) => item.email);

  type TUser = {
    email: string;
    name?: string;
  };
  const [users, setUsers] = useState<TUser[]>([]);

  useEffect(() => {
    let initUsers: TUser[] = [];
    initUsers.push(
      ...(managers ? managers.map((item) => ({ email: item.manager.email, name: item.manager.nickname })) : [])
    );
    initUsers.push(
      ...filtered_visitors.map((item) => {
        return { email: `${item.email}` };
      })
    );
    if (initUsers.length > 0) {
      setUsers(initUsers);
    }
  }, []);

  useEffect(() => {
    onChange?.(users.map((item) => item.email));
  }, [users]);

  return (
    <SectionContainer>
      <Label>
        {translate["참석자"][language]}{" "}
        <span
          css={css`
            color: ${theme.neutral400};
          `}
        >
          ({translate["선택"][language]})
        </span>
        <p
          css={css`
            color: ${theme.neutral400};
            font-weight: 500;
            font-size: 11px;
            margin-top: 4px;
          `}
        >
          *{translate["추가된 참석자들은 구글 캘린더 일정에 참석자로 설정됩니다."][language]}
        </p>
      </Label>
      <div
        css={css`
          display: flex;
          gap: 4px;
          white-space: normal;
          flex-wrap: wrap;
        `}
      >
        {users.map((item, index) => {
          return (
            <Button
              key={index}
              type="secondary"
              title={item.email}
              rightIcon={({ color }) => {
                return <Icon name="close" width={16} color={color} />;
              }}
              onClick={() => {
                setUsers((prev) => prev.filter((_, i) => i !== index));
              }}
              styles={{
                container: css`
                  border-radius: 100px;
                  height: 30px;
                  padding: 0 8px;
                  width: max-content;
                `,
                text: css`
                  font-size: 12px;
                  font-weight: 500;
                `,
              }}
            />
          );
        })}
      </div>
      <InputField
        value={keyword}
        placeholder={translate["참석자 검색"][language]}
        onChange={(value) => setKeyword(value)}
        onChangeDebounce={async (value) => {
          const res = await searchMembers(value);
          if (res.success) setSearchMember(res.data ?? []);
        }}
      />
      <div
        css={css`
          max-height: 400px;
          overflow: auto;
          width: 100%;
        `}
      >
        {searchMember.length === 0 && keyword && isValidEmail(keyword) && (
          <div
            onClick={() => {
              if (!users.map((item) => item.email).includes(keyword)) {
                setUsers([...users, { email: keyword }]);
              } else {
                setUsers((prev) => [...prev].filter((it) => it.email !== keyword));
              }
              setKeyword("");
              setSearchMember([]);
            }}
            css={css`
              width: calc(100% - 48px);
              border: none;
              display: flex;
              align-items: center;
              padding: 12px 24px;
              border-radius: 10px;
              gap: 8px;
              background-color: ${[...users].map((item) => item.email).includes(keyword) ? theme.neutral50 : ""};
              &:hover {
                background-color: ${theme.neutral50};
              }
            `}
          >
            {keyword}
            {[...users].map((item) => item.email).includes(keyword) && (
              <span
                css={css`
                  color: ${theme.primary500};
                  font-size: 14px;
                  font-weight: 500;
                  margin-left: auto;
                `}
              >
                {translate["이미 추가된 참석자입니다."][language]}
              </span>
            )}
          </div>
        )}
        {searchMember.map((item, index) => {
          return (
            <SearchManagerItem
              onClick={() => {
                if (!users.map((item) => item.email).includes(item.email)) {
                  setUsers([...users, { email: item.email, name: item.nickname }]);
                } else {
                  setUsers((prev) => [...prev].filter((it) => it.email !== item.email));
                }
                setKeyword("");
                setSearchMember([]);
              }}
              key={index}
              member={item}
              styles={{
                container: css`
                  width: calc(100% - 48px);
                  border: none;
                  background-color: ${[...users].map((item) => item.email).includes(item.email) ? theme.neutral50 : ""};

                  &:hover {
                    background-color: ${theme.neutral50};
                  }
                `,
              }}
              rightRender={() => {
                if ([...users].map((item) => item.email).includes(item.email)) {
                  return (
                    <p
                      css={css`
                        color: ${theme.primary500};
                        font-size: 14px;
                        font-weight: 500;
                        margin-left: auto;
                      `}
                    >
                      {translate["이미 추가된 참석자입니다."][language]}
                    </p>
                  );
                }
                return null;
              }}
            />
          );
        })}
      </div>
    </SectionContainer>
  );
};

export const MemoSection = ({ onChange }: { onChange?: (value: string) => void }) => {
  const { language } = useRecoilValue(util);
  return (
    <SectionContainer>
      <Label>
        {translate["메모"][language]}{" "}
        <span
          css={css`
            color: ${theme.neutral400};
          `}
        >
          ({translate["선택"][language]})
        </span>
      </Label>
      <InputField onChange={onChange} />
    </SectionContainer>
  );
};

export const ButtonGroup = ({
  onHandleRegister,
  onHandleSkip,
}: {
  onHandleRegister?: () => void;
  onHandleSkip?: () => void;
}) => {
  return (
    <div
      css={css`
        display: flex;
        gap: 12px;
      `}
    >
      <Button
        onClick={onHandleSkip}
        title="건너뛰기"
        type="secondary"
        styles={{
          container: css`
            flex: 1;
            height: 50px;
          `,
        }}
      />
      <Button
        onClick={onHandleRegister}
        title="등록"
        styles={{
          container: css`
            flex: 1;
            height: 50px;
          `,
        }}
      />
    </div>
  );
};

export default AddPersonalCalendar;
